<template>
  <AiEducationLayout>
    <EducationPageHeader title="家长工作台" description="查看当前账号已建立监护关系的孩子范围" />
    <ContentWrap>
      <EducationState v-if="status === 'loading'" state="loading" title="正在加载家长工作台" />
      <EducationState
        v-else-if="status === 'error'"
        state="error"
        title="家长工作台加载失败"
        description="请确认当前账号已配置有效的家长成员关系。"
        @retry="load"
      />
      <EducationState
        v-else-if="!childIds.length"
        state="empty"
        title="尚未关联孩子"
        description="请联系学校管理员建立并审核学生与家长关系。"
      />
      <template v-else>
        <el-alert
          title="孩子范围由后端监护关系确定，页面不能手动输入或切换到未授权学生。"
          type="info"
          :closable="false"
        />
        <el-table :data="children" class="children-table">
          <el-table-column label="关联学生" min-width="200">
            <template #default="{ row }">
              <div class="child-cell">
                <el-avatar :size="32" :src="row.avatar">{{ row.label.slice(0, 1) }}</el-avatar>
                <span>{{ row.label }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="学习时长" min-width="120">
            <template #default="{ row }">{{ formatMinutes(row.summary?.studySeconds) }}</template>
          </el-table-column>
          <el-table-column label="完成章节" min-width="100">
            <template #default="{ row }">{{ row.summary?.completedChapters ?? '—' }}</template>
          </el-table-column>
          <el-table-column label="已交作业" min-width="100">
            <template #default="{ row }">{{ row.summary?.assignmentsSubmitted ?? '—' }}</template>
          </el-table-column>
          <el-table-column label="答题正确率" min-width="120">
            <template #default="{ row }">{{ formatRate(row.summary?.correctRate) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template #default="{ row }">
              <el-button link type="primary" @click="openAssignments(row.id)">查看作业</el-button>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </ContentWrap>
  </AiEducationLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AiEducationLayout from '../layout/AiEducationLayout.vue'
import EducationPageHeader from '../components/EducationPageHeader.vue'
import EducationState from '../components/EducationState.vue'
import { useEducationWorkspace } from '../composables/useEducationWorkspace'
import {
  getParentChildren,
  getParentChildSummary,
  type ParentChildVO,
  type StudentSummaryVO
} from '@/api/aiEducation'

defineOptions({ name: 'AiEducationParentHome' })
const router = useRouter()
const { workspace, initialize } = useEducationWorkspace()
const status = ref<'loading' | 'ready' | 'error'>('loading')
const childIds = computed(() => workspace.value?.dataScope.childStudentIds || [])
const summaries = ref<Record<number, StudentSummaryVO>>({})
const childProfiles = ref<Record<number, ParentChildVO>>({})
const children = computed(() =>
  childIds.value.map((id) => ({
    id,
    label: childProfiles.value[id]?.nickname || `关联学生 #${id}`,
    avatar: childProfiles.value[id]?.avatar,
    summary: summaries.value[id]
  }))
)
const formatMinutes = (seconds?: number) =>
  seconds == null ? '—' : `${Math.round(seconds / 60)} 分钟`
const formatRate = (rate?: number) => (rate == null ? '—' : `${Math.round(rate * 100)}%`)
const openAssignments = (studentId: number) =>
  router.push({
    path: '/ai-Application-center/ai-education/parent/assignments',
    query: { studentId }
  })
const load = async () => {
  status.value = 'loading'
  try {
    await initialize()
    const profiles = await getParentChildren()
    childProfiles.value = Object.fromEntries(profiles.map((child) => [child.studentId, child]))
    const results = await Promise.allSettled(
      childIds.value.map(
        async (studentId) => [studentId, await getParentChildSummary(studentId)] as const
      )
    )
    summaries.value = Object.fromEntries(
      results.filter((result) => result.status === 'fulfilled').map((result) => result.value)
    )
    status.value = 'ready'
  } catch {
    status.value = 'error'
  }
}
onMounted(load)
</script>

<style scoped>
.children-table {
  margin-top: 18px;
}
.child-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
